import React, { useState } from 'react';
import { Client, Close, ContactUs, Dashboard, Exit, Logo, Progress, Settings, Subscription, Transaction } from 'assets';
import styles from './Sidebar.module.css';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { useNavigate, useLocation } from 'react-router-dom';
import { logout } from 'store/user/userSlice';
import { Controller, useForm } from 'react-hook-form';
import { Button } from 'components';
import { apiSendReport } from 'services';
import moment from 'moment';

interface ContactUsInput {
    email: string,
    subject: string,
    content: string
}

export default function Sidebar() {
    const userData = useAppSelector((state) => state.user.userData);
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useAppDispatch();

    const [message, setMessage] = useState<string | null>(null);
    const [isContactModalOpen, setIsContactModalOpen] = useState(false);
    const { handleSubmit, control, reset } = useForm<ContactUsInput>();

    const handleItemClick = (id: string) => {
        if (id === 'exit') {
            dispatch(logout());
            localStorage.clear();
        } else if (id === 'contact-us') {
            setIsContactModalOpen(true);
            return;
        }
        navigate(`/${id}`);
    };

    const handleCloseModal = () => {
        setIsContactModalOpen(false);
        setMessage('')
    };

    const menuItems = [
        { id: 'dashboard', icon: <Dashboard />, label: 'מסך ניהול' },
        { id: 'transactions', icon: <Transaction />, label: 'עסקאות' },
        { id: 'clients', icon: <Client />, label: 'לקוחות' },
        ...(userData?.userType !== "Regular" ? [{ id: 'report', icon: <Progress />, label: 'מעקב שכ”ט' }] : []),
        { id: 'settings', icon: <Settings />, label: 'הגדרות' },
        ...(userData?.userType !== "Regular" ? [{ id: 'packages', icon: <Subscription />, label: 'חבילות' }] : []),
        ...(userData?.userType !== "Regular" ? [{ id: 'textbox', icon: null, label: null }] : []),
        { id: 'contact-us', icon: <ContactUs />, label: 'עזרה' },
        { id: 'exit', icon: <Exit />, label: 'יציאה' },
    ];

    const onSubmit = async (data: ContactUsInput) => {
        try {
            if (!userData?.id || !userData?.company?.id) {
                setMessage('אירעה שגיאה, אנא נסה שוב מאוחר יותר.');
                return;
            }

            const payload = {
                userId: userData.id,
                subject: data.subject,
                content: data.content,
                company: userData.company.id,
            };

            const response = await apiSendReport(payload);

            if (response?.message) {
                setMessage(response.message);
                setTimeout(() => {
                    handleCloseModal()
                }, 2000);
                reset();
            } else {
                setMessage('אירעה שגיאה, אנא נסה שוב מאוחר יותר.');
            }
        } catch (error) {
            console.error("Error submitting contact request:", error);
            setMessage('אירעה שגיאה, אנא נסה שוב מאוחר יותר.');
        }
    };


    const getPackageName = () => {
        if (userData?.company) {
            const company = userData?.company;
            const today = new Date();
            const subscriptionDate = company?.Company_subscription
                ? new Date(company.Company_subscription)
                : null;

            if (subscriptionDate && today < subscriptionDate && company.packageCancel) {
                return `מנוי חודשי עד תאריך ${moment(company.Company_subscription).format("DD/MM/YYYY")}`;
            } else if (subscriptionDate && today < subscriptionDate && company.packageName === 'FreeTrial') {
                return 'חודש ניסיון חינם';
            } else if (subscriptionDate && today < subscriptionDate && company.packageName === 'MonthlyPackage') {
                return 'מנוי חודשי מתחדש';
            } else if (company?.CompanyAllowed > 0 && company?.packageName === 'TenTimes') {
                return `חבילה 10 עסקאות ${company?.CompanyAllowed}/10`;
            } else if (company?.CompanyAllowed > 0 && company?.packageName === 'OneTime') {
                return `עסקה חד פעמית ${company?.CompanyAllowed}/1`;
            }
        }
    }

    return (
        <>
            <div className={styles.sidebar}>
                <Logo style={{ cursor: 'pointer' }} onClick={() => navigate('/dashboard')} />
                <div className={styles.sidebarContent}>
                    {menuItems.map((item) =>
                        item.icon ? (
                            <div
                                key={item.id}
                                className={`${styles.sidebarItem} ${location.pathname.includes(`/${item.id}`) ? styles.active : ''}`}
                                onClick={() => handleItemClick(item.id)}
                            >
                                {React.cloneElement(item.icon, {
                                    style: {
                                        fill: location.pathname.includes(`/${item.id}`) ? '#317289' : '#A3A5BA',
                                    },
                                })}
                                <span className={styles.text} style={{ color: location.pathname.includes(`/${item.id}`) ? '#317289' : '#A3A5BA' }}>
                                    {item.label}
                                </span>
                            </div>
                        ) : (
                            <div key={item.id} className={styles.box}>
                                <span>מספר לקוח</span>
                                <span>{userData?.id}</span>
                                <span>{getPackageName()}</span>
                            </div>
                        )
                    )}
                </div>
            </div>

            {isContactModalOpen && (
                <div style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }} className={styles.modalOverlay}>
                    <div className={styles.modalContent}>
                        <button onClick={handleCloseModal} className="d-flex align-self-end">
                            <Close />
                        </button>
                        <h1>עזרה</h1>
                        <p>כתבו לנו, ונחזור אליכם בהקדם</p>
                        <Controller
                            name="email"
                            rules={{
                                pattern: {
                                    value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                                    message: 'אנא הכנס כתובת דוא״ל תקינה',
                                },
                            }}
                            control={control}
                            render={({ field, fieldState: { error } }) => (
                                <div className='d-flex align-items-center'>
                                    <label>דוא״ל</label>
                                    <input
                                        style={{ direction: 'ltr', textAlign: 'right' }}
                                        onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                                            const invalidChars = /[^a-zA-Z0-9@._%+\-]/;
                                            if (invalidChars.test(e.key)) {
                                                e.preventDefault();
                                            }
                                        }}
                                        onInput={(e: React.ChangeEvent<HTMLInputElement>) => {
                                            console.log(error);
                                        
                                            const value = e.target.value;
                                            const sanitizedValue = value.replace(/[^a-zA-Z0-9@._%+\-]/g, '');
                                            if (sanitizedValue !== value) {
                                                e.target.value = sanitizedValue;
                                            }
                                            field.onChange(e);
                                        }}
                                        maxLength={30}
                                        className={error ? styles.inputError : ''}
                                        {...field}
                                        type="email"
                                    />
                                </div>
                            )}
                        />

                        <Controller
                            name="subject"
                            control={control}
                            render={({ field }) => (
                                <div className='mt-4 d-flex align-items-center'>
                                    <label>נושא</label>
                                    <input
                                        {...field}
                                        type="text"
                                    />
                                </div>
                            )}
                        />

                        <Controller
                            name="content"
                            control={control}
                            render={({ field }) => (
                                <div className='mt-4 d-flex flex-column align-items-start'>
                                    <label>תיאור הפניה</label>
                                    <textarea
                                        maxLength={256}
                                        {...field}
                                    />
                                </div>
                            )}
                        />
                        <div className='mt-4 w-50 align-self-center'>
                            <Button onClick={handleSubmit(onSubmit)} type="button" text={'שליחה'} />
                        </div>
                        {message && (
                            <div className={styles.message} style={{ color: message === 'אירעה שגיאה, אנא נסה שוב מאוחר יותר.' ? 'red' : '#317289', marginTop: '10px' }}>
                                <b>{message}</b>
                            </div>
                        )}
                    </div>
                </div>
            )}

        </>
    );
}
